import React, { useState, useEffect } from 'react';

const ProjectResources = ({ project, onUpdate, saving }) => {
  const [resources, setResources] = useState([]);
  const [showAddResource, setShowAddResource] = useState(false);

  useEffect(() => {
    setResources(project.resources || []);
  }, [project]);

  const handleAddResource = (resourceData) => {
    const newResource = {
      id: Date.now(),
      ...resourceData,
      createdAt: new Date().toISOString()
    };

    const updatedResources = [...resources, newResource];
    setResources(updatedResources);
    onUpdate({ resources: updatedResources });
    setShowAddResource(false);
  };

  const handleRemoveResource = (resourceId) => {
    const updatedResources = resources.filter(r => r.id !== resourceId);
    setResources(updatedResources);
    onUpdate({ resources: updatedResources });
  };

  const getVisibilityBadge = (visibleTo) => {
    if (visibleTo.includes('estudiantes') && visibleTo.includes('apoderados')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Estudiantes y Apoderados
        </span>
      );
    } else if (visibleTo.includes('estudiantes')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Solo Estudiantes
        </span>
      );
    } else if (visibleTo.includes('apoderados')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          Solo Apoderados
        </span>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Recursos y Materiales</h3>
          <p className="text-sm text-gray-600">
            Agrega recursos que los estudiantes y apoderados podrán consultar
          </p>
        </div>
        <button
          onClick={() => setShowAddResource(true)}
          disabled={saving}
          className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Agregar Recurso</span>
        </button>
      </div>

      {resources.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <h4 className="text-lg font-medium text-gray-900 mb-2">Sin recursos agregados</h4>
          <p className="text-gray-600 mb-4">
            Agrega recursos y materiales que los estudiantes y apoderados podrán consultar
          </p>
          <button
            onClick={() => setShowAddResource(true)}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium"
          >
            Agregar Primer Recurso
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {resources.map((resource) => (
            <div key={resource.id} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{resource.title}</h4>
                    {resource.isRequired && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Obligatorio
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
                  
                  <div className="flex flex-wrap items-center space-x-2 mb-3">
                    {getVisibilityBadge(resource.visibleTo)}
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full capitalize">
                      {resource.type}
                    </span>
                  </div>

                  {resource.url && (
                    <div className="flex items-center space-x-2 text-sm">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <a 
                        href={resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-800 underline"
                      >
                        Ver recurso
                      </a>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleRemoveResource(resource.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddResource && (
        <AddResourceModal
          onSave={handleAddResource}
          onCancel={() => setShowAddResource(false)}
        />
      )}
    </div>
  );
};

const AddResourceModal = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'enlace',
    url: '',
    visibleTo: ['estudiantes', 'apoderados'],
    isRequired: false
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'El título es requerido';
    if (!formData.description.trim()) newErrors.description = 'La descripción es requerida';
    if (formData.type === 'enlace' && !formData.url.trim()) {
      newErrors.url = 'La URL es requerida para enlaces';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleVisibilityToggle = (group) => {
    setFormData(prev => ({
      ...prev,
      visibleTo: prev.visibleTo.includes(group)
        ? prev.visibleTo.filter(g => g !== group)
        : [...prev.visibleTo, group]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Agregar Recurso</h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título del Recurso *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ej: Guía de laboratorio de química"
            />
            {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descripción *</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Describe qué contiene este recurso..."
            />
            {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="enlace">Enlace Web</option>
                <option value="documento">Documento</option>
                <option value="video">Video</option>
                <option value="imagen">Imagen</option>
                <option value="libro">Libro/Manual</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isRequired}
                  onChange={(e) => handleChange('isRequired', e.target.checked)}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Recurso obligatorio</span>
              </label>
            </div>
          </div>

          {formData.type === 'enlace' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL del Enlace *</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => handleChange('url', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                  errors.url ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="https://ejemplo.com"
              />
              {errors.url && <p className="text-red-600 text-sm mt-1">{errors.url}</p>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Visible para</label>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.visibleTo.includes('estudiantes')}
                  onChange={() => handleVisibilityToggle('estudiantes')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Estudiantes</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.visibleTo.includes('apoderados')}
                  onChange={() => handleVisibilityToggle('apoderados')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Apoderados</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 rounded-lg"
            >
              Agregar Recurso
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectResources;